import React, { useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { convertUserTimeToUTC, convertUTCToUserTime, formatDateTime } from '../../utils/timeUtils';
import styles from './WeeklyRecapWorkflow.module.css';

const WeeklyRecapWorkflow = ({ prompt, onUpdateScheduling }) => {
    const { user } = useContext(AuthContext);
    const [editingStep, setEditingStep] = useState(null);
    const [editDateTime, setEditDateTime] = useState('');
    const [showPopover, setShowPopover] = useState(false);

    // Define the workflow steps
    const workflowSteps = [
        {
            id: 'prompt_generated',
            name: 'Prompt Generated',
            description: prompt.status === 'pending_generation' ? 'Waiting for prompt generation' : 'Weekly recap prompt created and stored',
            status: prompt.status === 'ready_for_review' || prompt.status === 'sent' ? 'completed' : 'pending',
            scheduledTime: null,
            completedTime: prompt.status === 'pending_generation' ? null : prompt.createdAt
        },
        {
            id: 'prompt_approved',
            name: 'Prompt Approved',
            description: 'Admin has approved the prompt package',
            status: prompt.promptApproved === true ? 'completed' : 'pending',
            scheduledTime: null,
            completedTime: prompt.promptApproved === true ? prompt.updatedAt : null
        },
        {
            id: 'llm_generated',
            name: 'LLM Generated',
            description: 'AI has generated the recap content',
            status: prompt.llmResponse && prompt.llmResponse.trim() !== '' ? 'completed' : 'pending',
            scheduledTime: null,
            completedTime: prompt.llmResponse && prompt.llmResponse.trim() !== '' ? prompt.updatedAt : null
        },
        {
            id: 'recap_approved',
            name: 'Recap Approved',
            description: 'Admin has approved the final recap',
            status: prompt.recapApproved === true ? 'completed' : 'pending',
            scheduledTime: null,
            completedTime: prompt.recapApproved === true ? prompt.updatedAt : null
        },
        {
            id: 'scheduled',
            name: 'Scheduled',
            description: prompt.status === 'pending_generation' ? 'Waiting for prompt generation before scheduling' : 'Recap is scheduled for broadcast',
            status: prompt.status === 'pending_generation' ? 'pending' : (prompt.scheduledSendTime && prompt.scheduledSendTime !== '00' && prompt.scheduledSendTime !== '0' ? 'completed' : 'pending'),
            scheduledTime: prompt.status === 'pending_generation' ? null : (prompt.scheduledSendTime && prompt.scheduledSendTime !== '00' && prompt.scheduledSendTime !== '0' ? prompt.scheduledSendTime : null),
            completedTime: prompt.status === 'pending_generation' ? null : (prompt.scheduledSendTime && prompt.scheduledSendTime !== '00' && prompt.scheduledSendTime !== '0' ? prompt.scheduledSendTime : null)
        },
        {
            id: 'sent',
            name: 'Sent',
            description: 'Recap has been broadcast to Discord',
            status: prompt.sentAt && prompt.sentAt !== '00' && prompt.sentAt !== '0' ? 'completed' : 'pending',
            scheduledTime: null,
            completedTime: prompt.sentAt && prompt.sentAt !== '00' && prompt.sentAt !== '0' ? prompt.sentAt : null
        }
    ];

    const getStepColor = (step) => {
        switch (step.status) {
            case 'completed':
                return '#28a745'; // Green
            case 'error':
                return '#dc3545'; // Red
            case 'pending':
            default:
                return '#6c757d'; // Grey
        }
    };

    const handleEditDateTime = (step) => {
        setEditingStep(step.id);
        setEditDateTime(step.scheduledTime ? convertUTCToUserTime(step.scheduledTime, user) : '');
        setShowPopover(true);
    };

    const handleSaveDateTime = async () => {
        if (!editDateTime) {
            // Delete scheduled time
            await onUpdateScheduling(prompt.weekNumber, prompt.season, null, null);
        } else {
            // Convert user timezone input to UTC for database storage
            const utcScheduledTime = convertUserTimeToUTC(editDateTime, user);
            const scheduledDate = new Date(utcScheduledTime);
            const now = new Date();

            if (scheduledDate <= now) {
                alert('Cannot schedule in the past. Please select a future date and time.');
                return;
            }

            // Update scheduled time (already in UTC)
            await onUpdateScheduling(prompt.weekNumber, prompt.season, utcScheduledTime, 'manual_edit');
        }

        setEditingStep(null);
        setEditDateTime('');
        setShowPopover(false);
    };

    const handleCancelEdit = () => {
        setEditingStep(null);
        setEditDateTime('');
        setShowPopover(false);
    };

    const getCurrentStatus = () => {
        const completedSteps = workflowSteps.filter(step => step.status === 'completed').length;
        const totalSteps = workflowSteps.length;

        if (completedSteps === totalSteps) return 'Complete';
        if (completedSteps === 0) {
            return prompt.status === 'pending_generation' ? 'Pending Generation' : 'Not Started';
        }
        return `In Progress (${completedSteps}/${totalSteps})`;
    };

    return (
        <div className={styles.workflowContainer}>
            <div
                className={styles.statusTrigger}
                onClick={() => setShowPopover(!showPopover)}
            >
                <span className={styles.statusText}>Status: {getCurrentStatus()}</span>
                <span className={styles.arrow}>{showPopover ? '▲' : '▼'}</span>
            </div>

            {showPopover && (
                <div className={styles.workflowPopover}>
                    <div className={styles.workflowHeader}>
                        <h4>Weekly Recap Workflow</h4>
                        <button
                            className={styles.closeButton}
                            onClick={() => setShowPopover(false)}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.workflowSteps}>
                        {workflowSteps.map((step, index) => (
                            <div key={step.id} className={styles.workflowStep}>
                                <div className={styles.stepIndicator}>
                                    <div
                                        className={styles.stepCircle}
                                        style={{ backgroundColor: getStepColor(step) }}
                                    >
                                        {step.status === 'completed' ? '✓' : index + 1}
                                    </div>
                                    {index < workflowSteps.length - 1 && (
                                        <div
                                            className={styles.stepConnector}
                                            style={{
                                                backgroundColor: step.status === 'completed' ? '#28a745' : '#e9ecef'
                                            }}
                                        />
                                    )}
                                </div>

                                <div className={styles.stepContent}>
                                    <div className={styles.stepHeader}>
                                        <h5 className={styles.stepName}>{step.name}</h5>
                                        <span
                                            className={styles.stepStatus}
                                            style={{ color: getStepColor(step) }}
                                        >
                                            {step.status === 'completed' ? 'Completed' : 'Pending'}
                                        </span>
                                    </div>

                                    <p className={styles.stepDescription}>{step.description}</p>

                                    {step.completedTime && (
                                        <small className={styles.completedTime}>
                                            Completed: {formatDateTime(step.completedTime, user)}
                                        </small>
                                    )}

                                    {step.scheduledTime && (
                                        <div className={styles.scheduledTime}>
                                            <small>
                                                Scheduled: {formatDateTime(step.scheduledTime, user)}
                                            </small>
                                            <button
                                                className={styles.editButton}
                                                onClick={() => handleEditDateTime(step)}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    )}

                                    {step.id === 'scheduled' && !step.scheduledTime && (
                                        <button
                                            className={styles.scheduleButton}
                                            onClick={() => handleEditDateTime(step)}
                                        >
                                            Schedule
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {editingStep && (
                        <div className={styles.editModal}>
                            <div className={styles.editModalContent}>
                                <h5>Edit Scheduled Time</h5>
                                <input
                                    type="datetime-local"
                                    value={editDateTime}
                                    onChange={(e) => setEditDateTime(e.target.value)}
                                    className={styles.dateTimeInput}
                                />
                                <div className={styles.editModalActions}>
                                    <button
                                        onClick={handleSaveDateTime}
                                        className={styles.saveButton}
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        className={styles.cancelButton}
                                    >
                                        Cancel
                                    </button>
                                    {editDateTime && (
                                        <button
                                            onClick={() => {
                                                setEditDateTime('');
                                                handleSaveDateTime();
                                            }}
                                            className={styles.deleteButton}
                                        >
                                            Delete Schedule
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default WeeklyRecapWorkflow;
